import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Plus } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { FormGrid, FormGroup, Label, Select, InfoLink, HintText } from '../style';
import { InfoLinkText } from '../../../OrderForm/Sections/MainInfos.style';
import QuickContactModal, { CompanyContact } from './QuickContactModal';

const ContactField: React.FC = () => {
  const { setValue, watch, getValues } = useFormContext<OrderSchemaType>();

  const [clientContacts, setClientContacts] = useState<CompanyContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | ''>('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [savedContactNotInList, setSavedContactNotInList] = useState<string | null>(null);
  const contactHydratedRef = useRef(false);

  const selectedClientId = watch('clientId');
  const selectedClient = watch('clientDetails');
  const selectedClientName = watch('client');

  const applyContactToForm = (contact: CompanyContact | undefined) => {
    setValue('contactId' as any, contact?.id ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactName' as any, contact?.name ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactEmail' as any, contact?.email ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactPhone' as any, contact?.phone ?? undefined, { shouldDirty: true, shouldValidate: false });
  };

  useEffect(() => {
    const details = selectedClient as any;
    const contacts = Array.isArray(details?.contacts)
      ? details.contacts.filter((c: any) => c && c.id != null)
      : [];
    setClientContacts(contacts);

    if (!selectedClientId) return;

    if (!contactHydratedRef.current) {
      contactHydratedRef.current = true;
      const savedContactId = getValues('contactId' as any) as number | undefined;
      const savedContactName = getValues('contactName' as any) as string | undefined;
      if (savedContactId && contacts.some((c: any) => Number(c.id) === Number(savedContactId))) {
        setSelectedContactId(Number(savedContactId));
        setSavedContactNotInList(null);
      } else {
        setSelectedContactId('');
        setSavedContactNotInList(savedContactId ? (savedContactName || null) : null);
      }
      return;
    }

    setSelectedContactId((prev) => {
      const stillValid = prev !== '' && contacts.some((c: any) => Number(c.id) === Number(prev));
      if (!stillValid) {
        applyContactToForm(undefined);
        setSavedContactNotInList(null);
      }
      return stillValid ? prev : '';
    });
  }, [selectedClientId, selectedClient]);

  return (
    <>
      <FormGrid>
        <FormGroup />
        <FormGroup />
        <FormGroup>
          <Label htmlFor="clientContactId">Contato</Label>
          <Select
            id="clientContactId"
            value={selectedContactId}
            disabled={!selectedClientId}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : '';
              setSelectedContactId(id);
              setSavedContactNotInList(null);
              const contact = id ? clientContacts.find((c) => Number(c.id) === id) : undefined;
              applyContactToForm(contact);
            }}
          >
            <option value="">Selecione</option>
            {clientContacts.map((contact) => (
              <option key={contact.id} value={contact.id}>{contact.name}</option>
            ))}
          </Select>
          {savedContactNotInList && (
            <HintText>Contato salvo no pedido: {savedContactNotInList} (não está mais na lista do cliente)</HintText>
          )}
          <InfoLink
            disabled={!selectedClientId}
            onClick={() => selectedClientId && setShowContactModal(true)}
          >
            <Plus size={12} />
            <InfoLinkText>Novo contato</InfoLinkText>
          </InfoLink>
        </FormGroup>
      </FormGrid>
      {showContactModal && !!selectedClientId && (
        <QuickContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          companyId={Number(selectedClientId)}
          companyName={selectedClientName || String((selectedClient as any)?.companyName || 'cliente selecionado')}
          onSaved={(contact) => {
            setClientContacts((prev) => [...prev, contact]);
            setSelectedContactId(contact.id);
            setSavedContactNotInList(null);
            applyContactToForm(contact);
            if (selectedClient) {
              const details = selectedClient as Record<string, any>;
              const contacts = Array.isArray(details.contacts) ? [...details.contacts, contact] : [contact];
              setValue('clientDetails', { ...details, contacts }, { shouldDirty: false, shouldValidate: false });
            }
          }}
        />
      )}
    </>
  );
};

export default ContactField;
